import { useCallback, useState } from 'react';
import {
  pushErrorFromAPI,
  pushSuccess,
  SdkErrorResponse,
} from 'src/utilities/notifications';
import { IErrorResponse } from 'src/utilities/shared-types';
import { IAddEditParentAdminFormData } from 'src/components/enterprise/Parents/Admin/types';
import { editParentAdmin } from 'src/components/enterprise/Parents/Admin/functions/editParentAdmin';
import { ROLES } from 'src/types/Employee';
import { getSsoUniqueIdentifier } from 'src/components/enterprise/Parents/Admin/functions/getSsoUniqueIdentifier';

export type UseEditParentAdminProps = {
  onSuccess?: () => void;
  onError?: () => void;
};
export type UseEditParentAdminReturn = {
  mutate: (
    corpId: string,
    dinerId: string,
    parentAdminFormData: IAddEditParentAdminFormData
  ) => Promise<void>;
  isLoading: boolean;
};

type UseEditParentAdmin = (
  props: UseEditParentAdminProps
) => UseEditParentAdminReturn;

export const useEditParentAdmin: UseEditParentAdmin = ({
  onSuccess,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>();

  const handleError = (
    error: IErrorResponse | SdkErrorResponse,
    parentAdminFormData: IAddEditParentAdminFormData
  ) => {
    pushErrorFromAPI(error, {
      fallbackMessage: `${parentAdminFormData.firstName} ${parentAdminFormData.lastName} was not updated. Try again.`,
    });
    setIsLoading(false);
    onError?.();
  };

  const handleSuccess = (parentAdminFormData: IAddEditParentAdminFormData) => {
    pushSuccess(`${parentAdminFormData.firstName}'s info has been updated.`);
    setIsLoading(false);
    onSuccess?.();
  };

  const mutate = useCallback<UseEditParentAdminReturn['mutate']>(
    async (corpId, dinerId, parentAdminFormData) => {
      try {
        setIsLoading(true);

        await editParentAdmin(corpId, dinerId, {
          first_name: parentAdminFormData.firstName,
          last_name: parentAdminFormData.lastName,
          email: parentAdminFormData.email,
          phone_number: parentAdminFormData.phoneNumber,
          internal_id: parentAdminFormData.internalId,
          role: ROLES.CORP_PARENT_ADMIN,
          corp_diner_settings: {
            sso_unique_identifier: getSsoUniqueIdentifier(parentAdminFormData),
          },
        });

        handleSuccess(parentAdminFormData);
      } catch (error) {
        handleError(error, parentAdminFormData);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    mutate,
    isLoading,
  };
};
